import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { FadeUp } from "@/components/site/Reveal";
import { FEATURES } from "@/config";
import { SCREEN_REGISTRY } from "@/components/showcase/registry";

const Device = ({ f, delay = 0 }) => {
    const [loaded, setLoaded] = useState(false);
    const [missing, setMissing] = useState(false);
    const imgRef = useRef(null);
    useEffect(() => {
        const i = imgRef.current;
        if (i && i.complete) {
            if (i.naturalWidth > 0) setLoaded(true);
            else setMissing(true);
        }
    }, []);
    const Mock = SCREEN_REGISTRY[f.key];
    const showImage = loaded && !missing;
    return (
        <div
            className="animate-float relative mx-auto mt-7 w-fit"
            style={{ animationDelay: `${delay}s` }}
        >
            <div className="absolute -inset-4 rounded-[2.4rem] bg-gradient-to-br from-[#3fcbf3]/25 via-transparent to-[#f0356f]/25 opacity-70 blur-xl" />
            <div className="relative rounded-[2.1rem] bg-gradient-to-b from-[#45d1f5]/70 via-white/15 to-[#f0356f]/60 p-[1.5px] shadow-[0_28px_70px_rgba(0,0,0,0.55)]">
                <div className="relative h-[380px] w-[196px] overflow-hidden rounded-[calc(2.1rem-1.5px)] bg-[#0b1422] sm:h-[440px] sm:w-[226px]">
                    {!missing && (
                        <img
                            ref={imgRef}
                            src={f.src}
                            alt={`SHREDxpro app — ${f.name}`}
                            loading="lazy"
                            decoding="async"
                            onLoad={() => setLoaded(true)}
                            onError={() => setMissing(true)}
                            className={`absolute inset-0 h-full w-full object-cover object-top transition-opacity duration-500 ${showImage ? "opacity-100" : "opacity-0"}`}
                        />
                    )}
                    {!showImage && Mock && <Mock />}
                    {!showImage && (
                        <div className="pointer-events-none absolute left-1/2 top-1.5 h-3.5 w-16 -translate-x-1/2 rounded-full bg-black/80" />
                    )}
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/[0.07] via-transparent to-transparent" />
                </div>
            </div>
        </div>
    );
};

const InsideShowcase = () => {
    const trackRef = useRef(null);
    const drag = useRef({ down: false, startX: 0, startScroll: 0 });
    const [idx, setIdx] = useState(0);
    const total = FEATURES.length;

    const cardStep = () => {
        const el = trackRef.current;
        if (!el || !el.children.length) return 320;
        return el.children[0].getBoundingClientRect().width + 20;
    };

    const onScroll = () => {
        const el = trackRef.current;
        if (!el) return;
        const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
        setIdx(atEnd ? total - 1 : Math.min(total - 1, Math.max(0, Math.round(el.scrollLeft / cardStep()))));
    };

    const go = (dir) => {
        trackRef.current?.scrollBy({ left: dir * cardStep(), behavior: "smooth" });
    };

    useEffect(() => {
        const el = trackRef.current;
        if (!el) return;
        const onWheel = (e) => {
            if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
            const atStart = el.scrollLeft <= 0 && e.deltaY < 0;
            const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 2 && e.deltaY > 0;
            if (atStart || atEnd) return;
            e.preventDefault();
            el.scrollLeft += e.deltaY;
        };
        el.addEventListener("wheel", onWheel, { passive: false });
        return () => el.removeEventListener("wheel", onWheel);
    }, []);

    const onPointerDown = (e) => {
        if (e.pointerType !== "mouse") return;
        drag.current = { down: true, startX: e.clientX, startScroll: trackRef.current.scrollLeft };
    };
    const onPointerMove = (e) => {
        if (!drag.current.down) return;
        trackRef.current.scrollLeft = drag.current.startScroll - (e.clientX - drag.current.startX);
    };
    const endDrag = () => {
        drag.current.down = false;
    };

    return (
        <section id="showcase" data-testid="inside-showcase" className="relative border-y border-white/5 bg-[#050a12] py-14 sm:py-20">
            <span id="features" className="absolute -top-16" aria-hidden="true" />
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <FadeUp>
                    <div className="flex flex-wrap items-end justify-between gap-6">
                        <div className="max-w-2xl">
                            <p className="eyebrow">Inside SHREDxpro</p>
                            <h2 className="font-display mt-4 text-4xl leading-[1.08] text-white sm:text-5xl lg:text-6xl">
                                Everything you need to train, fuel &amp; adapt.
                            </h2>
                            <p className="mt-6 text-base leading-relaxed text-[#9fb0c7]">
                                One intelligent fitness system designed to guide your workouts,
                                nutrition, progress and daily habits — all in one place.
                            </p>
                        </div>
                        <div className="flex items-center gap-4">
                            <span className="font-display text-lg text-white" data-testid="showcase-progress">
                                {String(idx + 1).padStart(2, "0")}
                                <span className="text-[#5c6b82]"> / {total}</span>
                            </span>
                            <button
                                data-testid="showcase-prev"
                                onClick={() => go(-1)}
                                aria-label="Previous feature"
                                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white transition-colors hover:border-[#3fcbf3]/60 hover:text-[#3fcbf3] disabled:opacity-30"
                                disabled={idx === 0}
                            >
                                <ChevronLeft className="h-5 w-5" />
                            </button>
                            <button
                                data-testid="showcase-next"
                                onClick={() => go(1)}
                                aria-label="Next feature"
                                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white transition-colors hover:border-[#3fcbf3]/60 hover:text-[#3fcbf3] disabled:opacity-30"
                                disabled={idx === total - 1}
                            >
                                <ChevronRight className="h-5 w-5" />
                            </button>
                        </div>
                    </div>
                </FadeUp>
            </div>

            <FadeUp delay={0.1}>
                <div
                    ref={trackRef}
                    onScroll={onScroll}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={endDrag}
                    onPointerLeave={endDrag}
                    data-testid="showcase-track"
                    className="mt-12 flex snap-x snap-mandatory gap-5 overflow-x-auto px-5 pb-4 sm:px-8 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                    style={{ scrollbarWidth: "none" }}
                >
                    {FEATURES.map((f, i) => (
                        <article
                            key={f.key}
                            data-testid={`inside-card-${f.num}`}
                            className={`w-[86vw] max-w-[360px] shrink-0 snap-center transition-[opacity,transform] duration-500 sm:max-w-[420px] ${
                                i === idx ? "opacity-100" : "opacity-45 sm:scale-[0.98]"
                            }`}
                        >
                            <div className="card-surface flex h-full flex-col p-6 sm:p-7">
                                <div className="flex items-center justify-between">
                                    <span className="rounded-full border border-[#3fcbf3]/25 bg-[#3fcbf3]/10 px-3 py-1 text-xs font-extrabold tracking-[0.2em] text-[#3fcbf3]">
                                        {f.num}
                                    </span>
                                    <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#7d8da3]">
                                        {f.name}
                                    </span>
                                </div>
                                <h3 className="font-display mt-4 text-2xl leading-snug text-white">{f.headline}</h3>
                                <p className="mt-2 text-sm leading-relaxed text-[#9fb0c7]">{f.desc}</p>
                                <Device f={f} />
                            </div>
                        </article>
                    ))}
                </div>
                <div className="mx-auto mt-6 max-w-7xl px-5 sm:px-8">
                    <div className="h-1 w-full rounded-full bg-white/5">
                        <div
                            className="h-full rounded-full bg-gradient-to-r from-[#45d1f5] to-[#f0356f] transition-all duration-300"
                            style={{ width: `${((idx + 1) / total) * 100}%` }}
                            data-testid="showcase-progress-bar"
                        />
                    </div>
                </div>
            </FadeUp>
        </section>
    );
};

export default InsideShowcase;
